import { useRef, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { LoaderCircleIcon } from 'lucide-react';

import FormInputWrapper from 'components/input/FormInputWrapper';
import { Flex } from 'components/layout';
import {
  Autocomplete,
  AutocompleteContent,
  AutocompleteInput,
  AutocompleteItem,
  AutocompleteList,
  AutocompleteStatus,
} from 'components/reui/autocomplete';
import { Typography } from 'components/typography';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { LocationSuggestion } from 'lib/api/getLocationSuggestions';
import { Maybe } from 'types/UtilityTypes';
import { cn } from 'utils/shadcn';

import { useLocationSuggestions } from '../hooks/useLocationSuggestions';
import {
  BatchQuoteFormValues,
  QuickQuoteInputs,
  QuickQuoteTextInputProps,
  QuoteCountries,
} from '../types';
import { QuickQuoteTextInput } from './QuickQuoteTextInput';

type LocationFormValues = QuickQuoteInputs & BatchQuoteFormValues;
type StopPath = `stops.${number}` | `quotes.${number}.stops.${number}`;
type QuickQuoteLocationInputProps = Omit<QuickQuoteTextInputProps, 'name'> & {
  name:
    `stops.${number}.location` | `quotes.${number}.stops.${number}.location`;
};

export const QuickQuoteLocationInput = (
  props: QuickQuoteLocationInputProps
) => {
  const { serviceFeaturesEnabled } = useServiceFeatures();

  if (!serviceFeaturesEnabled?.isQQLocationAutocompleteEnabled) {
    return <QuickQuoteTextInput {...props} />;
  }

  return <LocationCombobox {...props} />;
};

const LocationCombobox = ({
  name,
  label,
  required,
  placeholder,
  inputClassName,
  options,
  ...props
}: QuickQuoteLocationInputProps) => {
  const stopPath = getStopPath(name);
  // Location fields currently use only required validation; keep this prop out
  // of the label wrapper while matching QuickQuoteTextInput's public API.
  void options;
  const { control, setValue, getValues } = useFormContext<LocationFormValues>();
  const { field, fieldState } = useController({
    control,
    name,
    rules: required ? { required: 'Required' } : undefined,
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const value = String(field.value ?? '');
  const { suggestions, isLoading } = useLocationSuggestions(value);
  const [isOpen, setIsOpen] = useState(false);

  const clearSelectedLocation = () => {
    if (stopPath === null) {
      return;
    }

    const stop = getValues(stopPath);
    if (
      stop?.placeId === undefined &&
      !stop?.locationDisplay &&
      !stop?.city &&
      !stop?.state &&
      !stop?.zip
    ) {
      return;
    }

    const setOptions = { shouldDirty: true };
    setValue(`${stopPath}.placeId`, undefined, setOptions);
    setValue(`${stopPath}.locationDisplay`, '', setOptions);
    setValue(`${stopPath}.city`, '', setOptions);
    setValue(`${stopPath}.state`, '', setOptions);
    setValue(`${stopPath}.zip`, '', setOptions);
    setValue(`${stopPath}.country`, undefined, setOptions);
  };

  const selectSuggestion = (suggestion: LocationSuggestion) => {
    if (stopPath === null) {
      return;
    }

    const setOptions = {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    };
    setValue(name, suggestion.displayLabel, setOptions);
    setValue(
      `${stopPath}.locationDisplay`,
      suggestion.displayLabel,
      setOptions
    );
    setValue(`${stopPath}.city`, suggestion.city, setOptions);
    setValue(`${stopPath}.state`, suggestion.state, setOptions);
    setValue(`${stopPath}.zip`, suggestion.postalCode, setOptions);
    setValue(
      `${stopPath}.country`,
      suggestion.country === 'CA' ? QuoteCountries.Canada : QuoteCountries.USA,
      setOptions
    );
    setValue(`${stopPath}.placeId`, suggestion.placeId, setOptions);

    // Close the autocomplete dropdown and blur the input
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handleValueChange = (
    nextValue: string,
    details: { reason: string }
  ) => {
    // 'item-press' is Base UI's identifier for when the value is updated by
    // selecting an autocomplete option
    if (details.reason === 'item-press') {
      // Selecting an autocomplete option only provides its display label, so
      // find the full suggestion before selecting it. While this is prone to
      // duplicate labels, our backend has unique indexes for city/state/postal
      const selectedSuggestion = suggestions.find(
        (suggestion) => suggestion.displayLabel === nextValue
      );
      if (selectedSuggestion) {
        selectSuggestion(selectedSuggestion);
        return;
      }
    }

    // If value was updated by typing, clear previously selected location
    clearSelectedLocation();
    field.onChange(nextValue);
    setIsOpen(nextValue.trim() !== '');
  };

  return (
    <FormInputWrapper name={name} label={label} required={required} {...props}>
      <Autocomplete
        items={suggestions}
        value={value}
        onValueChange={handleValueChange}
        itemToStringValue={(suggestion: LocationSuggestion) =>
          suggestion.displayLabel
        }
        filter={null}
        autoHighlight='always'
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        <AutocompleteInput
          name={field.name}
          ref={(element) => {
            inputRef.current = element;
            field.ref(element);
          }}
          placeholder={placeholder}
          autoComplete='off'
          onBlur={field.onBlur}
          onFocus={() => setIsOpen(value.trim() !== '')}
          aria-invalid={fieldState.invalid}
          className={cn(
            inputClassName,
            fieldState.error && 'border-error-500 bg-error-50'
          )}
        />

        {value.trim() !== '' && (
          <AutocompleteContent>
            <AutocompleteStatus>
              {isLoading && (
                <Flex align='center' gap='xs'>
                  <LoaderCircleIcon
                    aria-hidden='true'
                    className='size-4 animate-spin'
                  />
                  <Typography variant='body-xs'>Searching...</Typography>
                </Flex>
              )}
              {!isLoading && suggestions.length === 0 && (
                <Typography variant='body-xxs'>No locations found</Typography>
              )}
            </AutocompleteStatus>
            <AutocompleteList aria-label={`${label} suggestions`}>
              {(suggestion: LocationSuggestion) => (
                <AutocompleteItem key={suggestion.placeId} value={suggestion}>
                  {suggestion.displayLabel}
                </AutocompleteItem>
              )}
            </AutocompleteList>
          </AutocompleteContent>
        )}
      </Autocomplete>
    </FormInputWrapper>
  );
};

function getStopPath(name: string): Maybe<StopPath> {
  const match = name.match(/^(stops\.\d+|quotes\.\d+\.stops\.\d+)\.location$/);
  return match ? (match[1] as StopPath) : null;
}
